/**
 * The copy of a support report for the organization's monday board — the
 * browser side (supportCaptureCore.ts is the tested core).
 *
 * When the person presses Send in the PanLuma window, `armReportCopy` has a
 * listener read what they submitted; PanLuma's own submit carries on untouched
 * and still gets the report even if this copy fails. After the click: if their
 * organization sends to a board (GET /support/target), CorpBrain takes its own
 * screenshot (only when "Include screenshot" was left on, with the html2canvas
 * the widget already loads) and POSTs the copy to /support/reports. The server
 * resolves the board from the sender's own organization.
 *
 * The console lines and the pages before are recorded from page load by
 * supportConsoleBoot.ts (imported first in main.tsx). The screenshot is
 * PanLuma's own as the person left it — annotations included — and ours
 * (html2canvas) only when that one cannot be read.
 */

import api from '@/services/api';
import type { User } from '@/types/models';
import { consoleRing, navigationRing } from './supportConsoleBoot';
import { MAX_FILE_BYTES, PANEL_IDS, dataUrlImage, fillReportForm, onPanelSend, type CapturedReport } from './supportCaptureCore';
import { currentSupportConversation } from './supportConversation';
import { fetchSupportTarget } from './supportTarget';

declare global {
  interface Window {
    html2canvas?: (element: HTMLElement, options?: Record<string, unknown>) => Promise<HTMLCanvasElement>;
  }
}

function browserDetails(): Record<string, string> {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    screen: `${window.screen.width}×${window.screen.height}`,
  };
}

async function screenshot(): Promise<Blob | null> {
  if (!window.html2canvas) return null;
  try {
    const canvas = await window.html2canvas(document.body, {
      logging: false,
      scale: Math.min(window.devicePixelRatio || 1, 1.5),
      ignoreElements: (el: Element) => !!el.id && el.id.startsWith('panluma-'),
    });
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.82));
    return blob && blob.size <= MAX_FILE_BYTES ? blob : null;
  } catch {
    return null;
  }
}

function sender(user: User) {
  return async (report: CapturedReport): Promise<void> => {
    if (!(await fetchSupportTarget(user.user_id))) return;
    // PanLuma's screenshot as they left it (with their marks); ours only when it cannot be read.
    const theirs = report.includeScreenshot && report.screenshotDataUrl ? dataUrlImage(report.screenshotDataUrl) : null;
    const usable = theirs && theirs.blob.size <= MAX_FILE_BYTES ? theirs : null;
    const shot = report.includeScreenshot ? (usable?.blob ?? await screenshot()) : null;
    const form = fillReportForm(new FormData(), report, {
      screenshot: shot, screenshotName: usable?.name, browser: browserDetails(), console: consoleRing.recent(),
    }) as FormData;
    await api.post('/support/reports', form);
  };
}

/**
 * Listen for Send on the panel the widget built (once per panel; the widget
 * reuses it). Capture phase and passive: PanLuma's own handlers run as before.
 */
export function armReportCopy(user: User | null): void {
  if (!user) return;
  const submit = document.getElementById(PANEL_IDS.submit);
  const form = document.getElementById(PANEL_IDS.form);
  if (!submit || !form) {
    console.warn('[support] PanLuma panel markup changed; no copy for the monday board');
    return;
  }
  void fetchSupportTarget(user.user_id);   // ready by the time they press Send
  const deps = {
    doc: document,
    pageUrl: () => window.location.href,
    // The pages BEFORE this one: the last entry is the page itself.
    context: () => ({ conversationId: currentSupportConversation(), history: navigationRing.recent().slice(0, -1) }),
    schedule: (task: () => void) => { window.setTimeout(task, 0); },
    send: sender(user),
    now: () => Date.now(),
    warn: (message: string) => console.warn(message),
  };
  // The panel is reused; listen with the user of THIS open.
  const holder = form as HTMLElement & { __corpbrainCopy?: (e: Event) => void };
  if (holder.__corpbrainCopy) {
    submit.removeEventListener('click', holder.__corpbrainCopy, true);
    form.removeEventListener('submit', holder.__corpbrainCopy, true);
  }
  holder.__corpbrainCopy = (event: Event) => onPanelSend(event, deps);
  submit.addEventListener('click', holder.__corpbrainCopy, { capture: true, passive: true });
  form.addEventListener('submit', holder.__corpbrainCopy, { capture: true, passive: true });
}
